import { useState, useEffect } from 'react';

const baht = (n) => (Number(n) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

const PRESETS = [
  { label: 'วันนี้', range: () => ({ startDate: today(), endDate: today() }) },
  { label: '7 วัน', range: () => ({ startDate: daysAgo(6), endDate: today() }) },
  { label: '30 วัน', range: () => ({ startDate: daysAgo(29), endDate: today() }) },
  { label: 'เดือนนี้', range: () => ({ startDate: monthStart(), endDate: today() }) }
];

const LEAK_LABELS = {
  waste: 'ของเสีย',
  variance: 'ผลต่างการนับ',
  internal: 'เบิกใช้',
  other: 'ปรับปรุงอื่นๆ'
};

function Kpi({ label, value, unit, hint, tone = 'normal' }) {
  const colors = { normal: '#667eea', good: '#27ae60', warn: '#f39c12', bad: '#e74c3c' };
  return (
    <div className="card" style={{ display: 'grid', gap: '0.35rem', alignContent: 'start' }}>
      <div style={{ fontSize: '0.85rem', color: '#aaa' }}>{label}</div>
      <div style={{ fontSize: '1.9rem', fontWeight: 700, color: colors[tone], lineHeight: 1.1 }}>
        {value}
        {unit && <span style={{ fontSize: '0.9rem', fontWeight: 500, marginLeft: '0.3rem', color: '#aaa' }}>{unit}</span>}
      </div>
      {hint && <div style={{ fontSize: '0.78rem', color: '#888' }}>{hint}</div>}
    </div>
  );
}

export default function CostReport({ apiUrl }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ startDate: today(), endDate: today() });

  useEffect(() => {
    loadReport();
  }, [dateRange]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams(dateRange);
      const response = await fetch(`${apiUrl}/api/stock/report/cost?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setReport(data.data);
    } catch (error) {
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div>กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div>
        <div className="page-header"><h2>💰 รายงานต้นทุน</h2></div>
        <div className="card">ไม่สามารถโหลดรายงานได้</div>
      </div>
    );
  }

  const { period, headcount, revenue, cost, kpi, costDrivers, alaCarte, leaks } = report;
  const leakTotal = cost.waste + cost.variance + cost.internal + cost.otherAdjustment;

  // Thai buffet food cost usually lands around 35-45%
  const costPctTone = kpi.foodCostPct === 0 ? 'normal'
    : kpi.foodCostPct > 50 ? 'bad'
    : kpi.foodCostPct > 45 ? 'warn'
    : 'good';

  return (
    <div>
      <div className="page-header">
        <h2>💰 รายงานต้นทุน</h2>
      </div>

      {/* Warnings */}
      {period.beforeCutover && (
        <div className="card" style={{ marginBottom: '1rem', borderLeft: '4px solid #f39c12' }}>
          <strong style={{ color: '#f39c12' }}>⚠️ ช่วงนี้มีข้อมูลก่อนตั้งต้นทุน</strong>
          <p style={{ fontSize: '0.9rem', color: '#bbb', marginTop: '0.4rem' }}>
            รายการที่บันทึกก่อน {new Date(period.cutoverDate).toLocaleDateString('th-TH')} ใช้ต้นทุนที่ยังตั้งไม่ถูก
            ต้นทุนถูกแช่แข็งไว้รายแถวจึงคำนวณย้อนหลังใหม่ไม่ได้ — ตัวเลขช่วงนั้นใช้อ้างอิงไม่ได้
          </p>
        </div>
      )}

      {revenue.mismatchedReceipts > 0 && (
        <div className="card" style={{ marginBottom: '1rem', borderLeft: '4px solid #e74c3c' }}>
          <strong style={{ color: '#e74c3c' }}>
            ⚠️ ใบเสร็จ {revenue.mismatchedReceipts} ใบ ยอดไม่ตรงกับที่ระบบคำนวณ
          </strong>
          <p style={{ fontSize: '0.9rem', color: '#bbb', marginTop: '0.4rem' }}>
            ไม่ได้บล็อกการเช็คเอาต์ แต่ควรตรวจดูว่าเป็นส่วนลดที่ตั้งใจหรือกรอกผิด
          </p>
        </div>
      )}

      {headcount.approximateReceipts > 0 && (
        <div className="card" style={{ marginBottom: '1rem', borderLeft: '4px solid #95a5a6' }}>
          <span style={{ fontSize: '0.9rem', color: '#bbb' }}>
            ℹ️ ใบเสร็จเก่า {headcount.approximateReceipts} ใบ ไม่ได้แยกประเภทลูกค้า — จำนวนหัวเป็นค่าประมาณ
          </span>
        </div>
      )}

      {/* Date range */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {PRESETS.map(p => {
            const r = p.range();
            const active = r.startDate === dateRange.startDate && r.endDate === dateRange.endDate;
            return (
              <button
                key={p.label}
                onClick={() => setDateRange(r)}
                className={active ? 'button' : 'button-secondary'}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>จากวันที่</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>ถึงวันที่</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* The four numbers that matter */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <Kpi
          label="ต้นทุนอาหารต่อหัว"
          value={baht(kpi.costPerHead)}
          unit="฿/คน"
          tone={kpi.costPerHead > kpi.buffetPrice ? 'bad' : 'normal'}
          hint={`บุฟเฟต์ ${baht(kpi.buffetPrice)} ฿ · ${headcount.total} คน (ผู้ใหญ่ ${headcount.adult} / เด็กโต ${headcount.childBig} / เด็กเล็ก ${headcount.childSmall}${headcount.childFree ? ` / ฟรี ${headcount.childFree}` : ''})`}
        />
        <Kpi
          label="% ต้นทุนอาหาร"
          value={kpi.foodCostPct}
          unit="%"
          tone={costPctTone}
          hint="ต้นทุนรวม ÷ ยอดขายรวม — เทียบข้ามวันได้"
        />
        <Kpi
          label="ยอดขายต่อหัว"
          value={baht(kpi.revenuePerHead)}
          unit="฿/คน"
          hint={`บุฟเฟต์ ${baht(revenue.buffet)} + รายจาน ${baht(revenue.alaCarte)}`}
        />
        <Kpi
          label="ต้นทุนสูญเปล่า"
          value={baht(leakTotal)}
          unit="฿"
          tone={leakTotal > 0 ? 'warn' : 'good'}
          hint={`${kpi.leakPct}% ของต้นทุนทั้งหมด`}
        />
      </div>

      {/* Cost breakdown */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>แยกต้นทุน</h3>
        <div style={{ display: 'grid', gap: '0.6rem' }}>
          {[
            ['ต้นทุนที่ขายออกไป', cost.food, '#27ae60'],
            ['ของเสีย', cost.waste, '#e74c3c'],
            ['ผลต่างจากการนับ', cost.variance, '#e67e22'],
            ['เบิกใช้ภายใน', cost.internal, '#8e44ad'],
            ['ปรับปรุงอื่นๆ', cost.otherAdjustment, '#95a5a6']
          ].map(([label, value, color]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#ccc' }}>
                <span style={{ color, marginRight: '0.5rem' }}>●</span>{label}
              </span>
              <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{baht(value)} ฿</span>
            </div>
          ))}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(255,255,255,0.15)',
            paddingTop: '0.6rem',
            fontWeight: 700
          }}>
            <span>รวม</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{baht(cost.total)} ฿</span>
          </div>
        </div>
      </div>

      {/* Top cost drivers */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>ต้นทุนสูงสุด</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="table-nowrap">
            <thead>
              <tr>
                <th>รายการ</th>
                <th style={{ textAlign: 'right' }}>ออกไป</th>
                <th style={{ textAlign: 'right' }}>ต้นทุนรวม</th>
                <th style={{ textAlign: 'right' }}>ต้นทุน/หัว</th>
              </tr>
            </thead>
            <tbody>
              {costDrivers.map(row => (
                <tr key={row.menuItemId}>
                  <td style={{ fontWeight: 600 }}>{row.name}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {row.quantityOut} {row.unit}
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{baht(row.cost)} ฿</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {baht(row.costPerHead)} ฿
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {costDrivers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              ยังไม่มีข้อมูลต้นทุนในช่วงนี้ — ต้องเปิดใช้สต็อกและตั้งต้นทุนก่อน
            </div>
          )}
        </div>
      </div>

      {/* À-la-carte P&L */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '0.4rem' }}>กำไรรายจาน</h3>
        <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1rem' }}>
          เฉพาะเมนูที่ตั้งราคาขายไว้ — เมนูบุฟเฟต์ไม่นับตรงนี้เพราะลูกค้าจ่ายต่อหัว
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table className="table-nowrap">
            <thead>
              <tr>
                <th>เมนู</th>
                <th style={{ textAlign: 'right' }}>ขายได้</th>
                <th style={{ textAlign: 'right' }}>รายได้</th>
                <th style={{ textAlign: 'right' }}>ต้นทุน</th>
                <th style={{ textAlign: 'right' }}>กำไร</th>
                <th style={{ textAlign: 'right' }}>มาร์จิ้น</th>
              </tr>
            </thead>
            <tbody>
              {alaCarte.map(row => (
                <tr key={row.menuItemId}>
                  <td style={{ fontWeight: 600 }}>{row.name}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.servingsSold}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{baht(row.revenue)} ฿</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{baht(row.cost)} ฿</td>
                  <td style={{
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    color: row.profit >= 0 ? '#27ae60' : '#e74c3c',
                    fontWeight: 600
                  }}>
                    {baht(row.profit)} ฿
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.margin}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {alaCarte.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              ยังไม่มีเมนูที่ตั้งราคาขาย — ตั้งราคาในหน้าจัดการเมนูก่อน
            </div>
          )}
        </div>
      </div>

      {/* Leaks */}
      {leaks.length > 0 && (
        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>รายการที่ไม่ได้ขาย</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="table-nowrap">
              <thead>
                <tr>
                  <th>รายการ</th>
                  <th>ประเภท</th>
                  <th style={{ textAlign: 'right' }}>จำนวน</th>
                  <th style={{ textAlign: 'right' }}>มูลค่า</th>
                </tr>
              </thead>
              <tbody>
                {leaks.map((row, i) => (
                  <tr key={`${row.menuItemId}-${i}`}>
                    <td style={{ fontWeight: 600 }}>{row.name}</td>
                    <td>{LEAK_LABELS[row.kind] || row.kind}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.quantity}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{baht(row.cost)} ฿</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
